import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  ChartTooltip,
} from '@elementary/common-ui/components';
import {
  formatShortDate,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Typography, useTheme } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  Dot,
  CartesianGrid,
} from 'recharts';
import { space } from '@elementary/common-ui/style/utils';
import { ScoreOverTimeChartPoints } from '../../types';

const YBar = styled(Flex)`
  width: 2px;
`;

type Props = {
  height: number;
  scores: ScoreOverTimeChartPoints[];
  lowThreshold: number;
  mediumThreshold: number;
  hideXAxis?: boolean;
  small?: boolean;
};

const percentsToPixels = (value: number, height: number) =>
  Math.round((height * value) / 100);

const filterDuplicateDates = (scores: ScoreOverTimeChartPoints[]) => {
  const datesMap: { [key: string]: number } = {};

  scores.forEach((record) => {
    const date = formatShortDate(localizeDate(record.date));
    datesMap[date] = record.score;
  });

  const filteredScoreDates = Object.keys(datesMap).map((date) => ({
    date,
    score: datesMap[date],
  }));

  return filteredScoreDates;
};

const ScoreOverTimeChart = ({
  height,
  scores,
  lowThreshold,
  mediumThreshold,
  hideXAxis = true,
  small,
}: Props) => {
  const theme = useTheme();
  const graphHeight = hideXAxis ? height : height - 12;
  const lowLength = percentsToPixels(lowThreshold, graphHeight);
  const mediumLength = percentsToPixels(
    mediumThreshold - lowThreshold,
    graphHeight,
  );
  const highLength = percentsToPixels(100 - mediumThreshold, graphHeight);
  const filteredScoreDates = filterDuplicateDates(scores);

  return (
    <Flex sx={{ position: 'relative' }}>
      <FlexColumn sx={{ position: 'absolute', left: '22px' }}>
        <YBar bgcolor={theme.palette.chart.score.high} height={highLength} />
        <YBar
          bgcolor={theme.palette.chart.score.medium}
          height={mediumLength}
        />
        <YBar
          bgcolor={theme.palette.chart.score.lowStrong}
          height={lowLength}
        />
      </FlexColumn>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={filteredScoreDates}>
          <CartesianGrid
            stroke={theme.palette.chart.grid}
            fill={theme.palette.icon.inverse}
          />

          {hideXAxis ? (
            <XAxis dataKey="date" hide />
          ) : (
            <XAxis
              dataKey="date"
              tickLine={false}
              tickMargin={1}
              tick={{
                fontSize: theme.tokens.fontSize.xxs,
                fill: theme.palette.icon.secondary,
              }}
              height={12}
              stroke={theme.palette.chart.marker}
              interval={filteredScoreDates?.length > 8 && small ? 1 : 0}
              padding={{ right: 8 }}
            />
          )}
          <YAxis
            domain={[0, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: theme.tokens.fontSize.xxs }}
            tickMargin={4}
            width={22}
            ticks={[lowThreshold, mediumThreshold, 100]}
          />

          <Line
            type="linear"
            dataKey="score"
            stroke={theme.palette.icon.secondary}
            dot={<Dot />}
          />

          <ReferenceLine
            y={lowThreshold}
            stroke={theme.palette.chart.axis}
            strokeDasharray="5"
          />
          <ReferenceLine
            y={mediumThreshold}
            stroke={theme.palette.chart.axis}
            strokeDasharray="5"
          />
          <Tooltip
            content={({ payload }) => {
              const data =
                payload && payload.length ? payload[0].payload : null;
              const color =
                data?.score < lowThreshold
                  ? theme.palette.chart.score.lowStrong
                  : data?.score > mediumThreshold
                  ? theme.palette.chart.score.high
                  : theme.palette.chart.score.medium;
              return (
                <ChartTooltip>
                  <FlexColumn sx={{ gap: space('sm') }}>
                    <Typography>{data?.date}</Typography>
                    <Typography sx={{ color }}>
                      Score: {data?.score}%
                    </Typography>
                  </FlexColumn>
                </ChartTooltip>
              );
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Flex>
  );
};

export default ScoreOverTimeChart;
